import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { generateAnalyticsReadinessPDF } from '../../../utils/roiPdfGenerator';

const AnalyticsReadinessChecker = ({ onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    companySize: '',
    industry: '',
    currentTools: [],
    dataQuality: '',
    teamSkills: '',
    budget: '',
    timeline: '',
    objectives: [],
    contactInfo: {
      name: '',
      email: '',
      company: '',
      role: ''
    }
  });
  const [results, setResults] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const companySizeOptions = [
    { value: 'startup', label: 'Startup (1-50 employees)' },
    { value: 'small', label: 'Small Business (51-200 employees)' },
    { value: 'medium', label: 'Medium Enterprise (201-1000 employees)' },
    { value: 'large', label: 'Large Enterprise (1000+ employees)' }
  ];

  const industryOptions = [
    { value: 'technology', label: 'Technology & Software' },
    { value: 'financial', label: 'Financial Services' },
    { value: 'healthcare', label: 'Healthcare & Life Sciences' },
    { value: 'retail', label: 'Retail & E-commerce' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'consulting', label: 'Professional Services' },
    { value: 'other', label: 'Other' }
  ];

  const dataQualityOptions = [
    { value: 'poor', label: 'Poor - Significant data quality issues' },
    { value: 'fair', label: 'Fair - Some data quality concerns' },
    { value: 'good', label: 'Good - Generally reliable data' },
    { value: 'excellent', label: 'Excellent - High-quality, clean data' }
  ];

  const teamSkillsOptions = [
    { value: 'basic', label: 'Basic - Limited analytics skills' },
    { value: 'intermediate', label: 'Intermediate - Some analytics experience' },
    { value: 'advanced', label: 'Advanced - Strong analytics capabilities' },
    { value: 'expert', label: 'Expert - Data science and ML expertise' }
  ];

  const budgetOptions = [
    { value: 'under50k', label: 'Under €50,000' },
    { value: '50k-100k', label: '€50,000 - €100,000' },
    { value: '100k-250k', label: '€100,000 - €250,000' },
    { value: '250k-500k', label: '€250,000 - €500,000' },
    { value: 'over500k', label: 'Over €500,000' }
  ];

  const timelineOptions = [
    { value: '3months', label: '3 months or less' },
    { value: '6months', label: '3-6 months' },
    { value: '12months', label: '6-12 months' },
    { value: 'over12months', label: 'Over 12 months' }
  ];

  const currentToolsList = [
    { id: 'excel', label: 'Microsoft Excel' },
    { id: 'powerbi', label: 'Power BI' },
    { id: 'tableau', label: 'Tableau' },
    { id: 'googleanalytics', label: 'Google Analytics' },
    { id: 'salesforce', label: 'Salesforce Analytics' },
    { id: 'python', label: 'Python/R' },
    { id: 'sql', label: 'SQL Databases' },
    { id: 'none', label: 'No formal analytics tools' }
  ];

  const objectivesList = [
    { id: 'reporting', label: 'Improve reporting and dashboards' },
    { id: 'insights', label: 'Generate actionable business insights' },
    { id: 'automation', label: 'Automate data processes' },
    { id: 'prediction', label: 'Predictive analytics and forecasting' },
    { id: 'realtime', label: 'Real-time data monitoring' },
    { id: 'compliance', label: 'Data governance and compliance' }
  ];

  const calculateReadiness = () => {
    setIsCalculating(true);
    
    setTimeout(() => {
      // Scoring system (0-100)
      let totalScore = 0;
      let maxScore = 0;

      // Company size scoring (0-15 points)
      const sizeScores = { startup: 8, small: 10, medium: 13, large: 15 };
      const sizeScore = sizeScores?.[formData?.companySize] || 0;
      totalScore += sizeScore;
      maxScore += 15;

      // Data quality scoring (0-25 points)
      const qualityScores = { poor: 5, fair: 12, good: 20, excellent: 25 };
      const qualityScore = qualityScores?.[formData?.dataQuality] || 0;
      totalScore += qualityScore;
      maxScore += 25;

      // Team skills scoring (0-20 points)
      const skillsScores = { basic: 5, intermediate: 10, advanced: 16, expert: 20 };
      const skillsScore = skillsScores?.[formData?.teamSkills] || 0;
      totalScore += skillsScore;
      maxScore += 20;

      // Current tools scoring (0-15 points)
      const toolsScore = Math.min(formData?.currentTools?.length * 2, 15);
      totalScore += toolsScore;
      maxScore += 15;

      // Budget scoring (0-15 points)
      const budgetScores = { 'under50k': 5, '50k-100k': 8, '100k-250k': 12, '250k-500k': 14, 'over500k': 15 };
      const budgetScore = budgetScores?.[formData?.budget] || 0;
      totalScore += budgetScore;
      maxScore += 15;

      // Objectives scoring (0-10 points)
      const objectivesScore = Math.min(formData?.objectives?.length * 2, 10);
      totalScore += objectivesScore;
      maxScore += 10;

      const readinessPercentage = Math.round((totalScore / maxScore) * 100);
      
      // Determine readiness level
      let readinessLevel, recommendation, priority;
      if (readinessPercentage >= 80) {
        readinessLevel = 'High';
        recommendation = 'Ready for advanced analytics implementation';
        priority = 'Immediate implementation recommended';
      } else if (readinessPercentage >= 60) {
        readinessLevel = 'Medium';
        recommendation = 'Good foundation, some improvements needed';
        priority = 'Implementation within 3-6 months';
      } else if (readinessPercentage >= 40) {
        readinessLevel = 'Low';
        recommendation = 'Significant preparation required';
        priority = 'Foundation building phase needed';
      } else {
        readinessLevel = 'Very Low';
        recommendation = 'Extensive groundwork required';
        priority = 'Strategic planning phase required';
      }

      // Calculate improvement costs
      const gapScore = maxScore - totalScore;
      const improvementCost = gapScore * 2500; // €2,500 per gap point
      const timeToReadiness = Math.ceil(gapScore / 10); // Months

      setResults({
        readinessPercentage,
        readinessLevel,
        recommendation,
        priority,
        totalScore,
        maxScore,
        categoryScores: {
          size: { score: sizeScore, max: 15, percentage: Math.round((sizeScore / 15) * 100) },
          quality: { score: qualityScore, max: 25, percentage: Math.round((qualityScore / 25) * 100) },
          skills: { score: skillsScore, max: 20, percentage: Math.round((skillsScore / 20) * 100) },
          tools: { score: toolsScore, max: 15, percentage: Math.round((toolsScore / 15) * 100) },
          budget: { score: budgetScore, max: 15, percentage: Math.round((budgetScore / 15) * 100) },
          objectives: { score: objectivesScore, max: 10, percentage: Math.round((objectivesScore / 10) * 100) }
        },
        improvementCost,
        timeToReadiness,
        gapScore
      });
      
      setIsCalculating(false);
      setCurrentStep(4);
    }, 2000);
  };

  const handleInputChange = (field, value) => {
    if (field?.includes('.')) {
      const [parent, child] = field?.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev?.[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleCheckboxChange = (field, itemId, checked) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked 
        ? [...prev?.[field], itemId]
        : prev?.[field]?.filter(id => id !== itemId)
    }));
  };

  const handleDownloadReport = () => {
    generateAnalyticsReadinessPDF(results, formData);
    if (onComplete) {
      onComplete(results);
    }
  };

  const nextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(amount);
  };

  const getReadinessColor = (level) => {
    switch (level) {
      case 'High': return 'text-success';
      case 'Medium': return 'text-warning';
      case 'Low': return 'text-error';
      case 'Very Low': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-primary mb-4">Organization Profile</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Company Size"
                  options={companySizeOptions}
                  value={formData?.companySize}
                  onChange={(value) => handleInputChange('companySize', value)}
                  required
                />
                <Select
                  label="Industry"
                  options={industryOptions}
                  value={formData?.industry}
                  onChange={(value) => handleInputChange('industry', value)}
                  required
                />
                <Select
                  label="Data Quality Assessment"
                  options={dataQualityOptions}
                  value={formData?.dataQuality}
                  onChange={(value) => handleInputChange('dataQuality', value)}
                  required
                />
                <Select
                  label="Team Analytics Skills"
                  options={teamSkillsOptions}
                  value={formData?.teamSkills}
                  onChange={(value) => handleInputChange('teamSkills', value)}
                  required
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-primary mb-4">Current Capabilities & Resources</h3>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-3">
                    Current Analytics Tools (Select all that apply)
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {currentToolsList?.map((tool) => (
                      <Checkbox
                        key={tool?.id}
                        label={tool?.label}
                        checked={formData?.currentTools?.includes(tool?.id)}
                        onChange={(e) => handleCheckboxChange('currentTools', tool?.id, e?.target?.checked)}
                      />
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Select
                    label="Available Budget"
                    options={budgetOptions}
                    value={formData?.budget}
                    onChange={(value) => handleInputChange('budget', value)}
                    required
                  />
                  <Select
                    label="Implementation Timeline"
                    options={timelineOptions}
                    value={formData?.timeline}
                    onChange={(value) => handleInputChange('timeline', value)}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-3">
                    Analytics Objectives (Select all that apply)
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {objectivesList?.map((objective) => (
                      <Checkbox
                        key={objective?.id}
                        label={objective?.label}
                        checked={formData?.objectives?.includes(objective?.id)}
                        onChange={(e) => handleCheckboxChange('objectives', objective?.id, e?.target?.checked)}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-primary mb-4">Contact Information</h3>
              <p className="text-muted-foreground mb-6">
                Receive your personalized analytics readiness assessment report.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Full Name"
                  type="text"
                  placeholder="Alex Johnson"
                  value={formData?.contactInfo?.name}
                  onChange={(e) => handleInputChange('contactInfo.name', e?.target?.value)}
                  required
                />
                <Input
                  label="Email Address"
                  type="email"
                  placeholder="alex@company.com"
                  value={formData?.contactInfo?.email}
                  onChange={(e) => handleInputChange('contactInfo.email', e?.target?.value)}
                  required
                />
                <Input
                  label="Company Name"
                  type="text"
                  placeholder="Data Insights Corp"
                  value={formData?.contactInfo?.company}
                  onChange={(e) => handleInputChange('contactInfo.company', e?.target?.value)}
                  required
                />
                <Input
                  label="Job Title"
                  type="text"
                  placeholder="Analytics Manager"
                  value={formData?.contactInfo?.role}
                  onChange={(e) => handleInputChange('contactInfo.role', e?.target?.value)}
                  required
                />
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            {isCalculating ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 border-4 border-secondary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <h3 className="text-lg font-semibold text-primary mb-2">Analyzing Your Analytics Readiness...</h3>
                <p className="text-muted-foreground">
                  Evaluating capabilities against industry benchmarks
                </p>
              </div>
            ) : results && (
              <div>
                <h3 className="text-lg font-semibold text-primary mb-6">Your Analytics Readiness Assessment</h3>
                
                {/* Overall Score */}
                <div className="bg-gradient-to-br from-secondary/10 to-secondary/5 rounded-lg p-8 text-center mb-8">
                  <div className="text-6xl font-bold text-secondary mb-2">
                    {results?.readinessPercentage}%
                  </div>
                  <div className={`text-xl font-semibold mb-2 ${getReadinessColor(results?.readinessLevel)}`}>
                    {results?.readinessLevel} Readiness
                  </div>
                  <div className="text-muted-foreground">
                    {results?.recommendation}
                  </div>
                </div>

                {/* Category Breakdown */}
                <div className="bg-surface rounded-lg p-6 mb-6">
                  <h4 className="font-semibold text-primary mb-4">Readiness Breakdown</h4>
                  <div className="space-y-4">
                    {Object.entries(results?.categoryScores)?.map(([category, data]) => (
                      <div key={category} className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium capitalize">
                            {category === 'size' ? 'Organization Size' : 
                             category === 'quality' ? 'Data Quality' :
                             category === 'skills' ? 'Team Skills' :
                             category === 'tools' ? 'Current Tools' :
                             category === 'budget'? 'Budget Allocation' : 'Objectives Clarity'}
                          </span>
                          <span className="text-sm font-semibold">
                            {data?.score}/{data?.max} ({data?.percentage}%)
                          </span>
                        </div>
                        <div className="w-full bg-border rounded-full h-2">
                          <div 
                            className="bg-secondary h-2 rounded-full transition-all duration-300"
                            style={{ width: `${data?.percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Improvement Plan */}
                <div className="bg-white border border-secondary/20 rounded-lg p-6 mb-6">
                  <h4 className="font-semibold text-primary mb-4">Improvement Plan</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-warning mb-1">
                        {formatCurrency(results?.improvementCost)}
                      </div>
                      <div className="text-sm text-muted-foreground">Estimated Investment</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-accent mb-1">
                        {results?.timeToReadiness} months
                      </div>
                      <div className="text-sm text-muted-foreground">Time to Full Readiness</div>
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    <strong>Priority:</strong> {results?.priority}
                  </div>
                </div>

                {/* Next Steps */}
                <div className="bg-white border border-secondary/20 rounded-lg p-6">
                  <h4 className="font-semibold text-primary mb-3">Recommended Next Steps</h4>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center space-x-2">
                      <Icon name="Target" size={16} color="var(--color-secondary)" />
                      <span className="text-sm">Download detailed readiness report with gap analysis</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="Users" size={16} color="var(--color-secondary)" />
                      <span className="text-sm">Schedule team capability assessment workshop</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="Map" size={16} color="var(--color-secondary)" />
                      <span className="text-sm">Develop customized analytics implementation roadmap</span>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      variant="default"
                      iconName="Download"
                      iconPosition="left"
                      className="bg-secondary hover:bg-secondary/90"
                      onClick={handleDownloadReport}
                    >
                      Download Report
                    </Button>
                    <Button
                      variant="outline"
                      iconName="Calendar"
                      iconPosition="left"
                    >
                      Book Assessment
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-primary">Analytics Readiness Checker</h2>
            <p className="text-sm text-muted-foreground">
              Assess your organization's readiness for advanced analytics implementation
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-surface rounded-lg transition-colors duration-200"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-4 bg-surface">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-primary">
              Step {currentStep} of 4
            </span>
            <span className="text-sm text-muted-foreground">
              {Math.round((currentStep / 4) * 100)}% Complete
            </span>
          </div>
          <div className="w-full bg-border rounded-full h-2">
            <div 
              className="bg-secondary h-2 rounded-full transition-all duration-300 progress-bar"
              style={{ width: `${(currentStep / 4) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {renderStep()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border bg-surface">
          <Button
            variant="outline"
            onClick={prevStep}
            iconName="ChevronLeft"
            iconPosition="left"
            className="hover:scale-105 hover:shadow-md focus:ring-2 focus:ring-secondary focus:ring-offset-2 active:scale-95 transition-all duration-200"
          >
            Previous
          </Button>
          
          <div className="flex space-x-2">
            {[1, 2, 3, 4]?.map((step) => (
              <div
                key={step}
                className={`w-2 h-2 rounded-full transition-colors duration-200 ${
                  step === currentStep ? 'bg-secondary' : 
                  step < currentStep ? 'bg-secondary/50' : 'bg-border'
                }`}
              />
            ))}
          </div>

          {currentStep < 3 ? (
            <Button
              variant="default"
              onClick={nextStep}
              iconName="ChevronRight"
              iconPosition="right"
              className="bg-secondary hover:bg-secondary/90 hover:scale-105 hover:shadow-elevation focus:ring-2 focus:ring-secondary focus:ring-offset-2 active:scale-95 transition-all duration-200"
            >
              Next Step
            </Button>
          ) : (
            <Button
              variant="default"
              onClick={calculateReadiness}
              loading={isCalculating}
              iconName="BarChart3"
              iconPosition="right"
              className="bg-secondary hover:bg-secondary/90 hover:scale-105 hover:shadow-elevation focus:ring-2 focus:ring-secondary focus:ring-offset-2 active:scale-95 transition-all duration-200"
            >
              Check Readiness
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsReadinessChecker;